import _ from "lodash";
import React, { useState, useMemo, useRef, useContext } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { withRouter } from "react-router";
import classes from "./ReportV2.scss";
import { Avatar, DSProvider } from "@toddle-edu/ds-web";
import { JournalOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";

import { useI18n } from "Hooks";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import {
  SUBMISSION_TYPE_IB_DEFINED,
  SUBMISSION_TYPE_FINAL,
  SUBMISSION_TYPE_DRAFT,
} from "Projects/Constants/stringConstants";
import UseTemplateMediaCard from "Projects/components/UseTemplateMediaCard";
import {
  showUseTemplateErrors,
  getCurrentProjectGroupDetails,
  ProjectPlannerContext,
} from "Projects/modules/ProjectModules";
import {
  mapProjectGroupSubmissionWithFields,
  runAutoCheckSimilarity,
} from "Projects/modules/Utils";

import { getProjectIBDefinedTasksQuery } from "./modules/ReportV2Queries";
import ReportItem from "./components/ReportItem";
import {
  updateProjectDeadlineSubmission,
  updateProjectDeadline,
  createAttachment,
  deleteAttachment,
  updateAttachment,
  updateSubmissionItemResponse,
  createProjectTemplateCopy,
} from "./modules/ReportV2Modules";
import ReportSubmissionViewer from "./components/ReportSubmissionViewer";
import { getUserType } from "modules/CommonSelectors";
import { isUserTypeStudentOrParent } from "Utils";

const EMPTY_UPLOADS = {};

const styles = {
  useTemplateContainerStyle: {
    maxWidth: "492px",
    width: "492px",
  },
};

const ReportV2 = props => {
  const {
    template: {
      body: { nodes },
    },
    projectTasks,
    projectGroupId,
    getProjectIBDefinedTasksFilters,
    updateProjectDeadline,
    updateProjectDeadlineSubmission,
    deleteAttachment,
    updateAttachment,
    createAttachment,
    projectGroupType,
    studentDetails,
    updateSubmissionItemResponse,
    disabled: projectDisabled,
    isUserStudentOrParent,
    projectId,
    selectedProjectReportTemplate,
    containerStyle,
    showHeaderContainer = true,
    renderBottomComp,
    projectGroupSubType,
    showUseTemplateErrors,
    progressOfUploads,
  } = props;

  const { t } = useI18n(["common", "project"]);

  const pageTitle = t(nodes["report"]?.label);

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [viewModalDetails, setViewModalDetails] = useState({});
  const autoCheckSimilarityRef = useRef({});
  const { trackPendingTurnInSubmissions } = useContext(ProjectPlannerContext);

  const onUpdateProjectDeadline = async ({
    input: { deadlineIds, isComplete },
  }) => {
    setShowFullScreenLoader(true);

    await updateProjectDeadline({
      input: {
        taskIds: deadlineIds,
        projectGroupId,
        isComplete,
      },
      extraParams: {
        vars: { getProjectIBDefinedTasksFilters },
      },
    });
    setShowFullScreenLoader(false);
  };

  const onUpdateProjectDeadlineSubmission = async ({
    input: { submissionIds, propertiesToUpdate },
  }) => {
    setShowFullScreenLoader(true);

    await updateProjectDeadlineSubmission({
      input: {
        submissionIds,
        ...propertiesToUpdate,
        projectGroupId,
      },
      extraParams: {
        vars: { getProjectIBDefinedTasksFilters },
      },
    });
    if (propertiesToUpdate.isTurnedIn) {
      trackPendingTurnInSubmissions({
        submissionStatusDetails: {
          submissionIds,
          isTurnedIn: propertiesToUpdate.isTurnedIn,
        },
      });
    }
    setShowFullScreenLoader(false);
  };

  const onDeleteAttachment = async ({ attachmentIds, submissionId }) => {
    setShowFullScreenLoader(true);
    await deleteAttachment({
      input: {
        attachmentIds,
      },
      extraParams: {
        vars: { getProjectIBDefinedTasksFilters },
      },
    });
    trackPendingTurnInSubmissions({
      attachmentDetails: {
        submissionId,
        removedAttachmentIds: attachmentIds,
      },
    });
    setShowFullScreenLoader(false);
  };

  const onCreateAttachment = async ({
    attachments,
    parentId,
    submissionId,
  }) => {
    setShowFullScreenLoader(true);

    const updatedAttachments = _.map(attachments, item => {
      return {
        ...item,
        parentId,
        parentType: "PROJECT_SUBMISSION_ITEM_RESPONSE",
      };
    });

    const createdAttachments = await createAttachment({
      input: [...updatedAttachments],
      extraParams: {
        vars: { getProjectIBDefinedTasksFilters },
      },
    });
    trackPendingTurnInSubmissions({
      attachmentDetails: {
        submissionId,
        addedAttachmentIds: _.map(createdAttachments, "id"),
      },
    });
    if (autoCheckSimilarityRef?.current) {
      runAutoCheckSimilarity({
        attachments: createdAttachments,
        similarityRef: autoCheckSimilarityRef,
      });
    }

    setShowFullScreenLoader(false);
  };

  const onUpdateAttachment = async ({ attachmentToUpdate }) => {
    setShowFullScreenLoader(true);

    await updateAttachment({
      input: {
        ..._.pick(attachmentToUpdate, ["id", "name"]),
      },
      extraParams: {
        vars: { getProjectIBDefinedTasksFilters },
      },
    });
    setShowFullScreenLoader(false);
  };

  const onUpdateInputDebounced = useMemo(() => {
    return _.debounce(async ({ submissionResponseId, value }) => {
      await updateSubmissionItemResponse({
        input: {
          id: submissionResponseId,
          value,
        },
      });
    }, 300);
  }, []);

  const onUpdateInputField = async ({ submissionResponseId, value }) => {
    onUpdateInputDebounced({ submissionResponseId, value });
  };

  const onCloseModal = () => {
    setViewModalDetails({ displayModal: false });
  };

  /**   code for use template */
  // TODO Jatin, will be updated after be updated

  const draftProjectTask = _.find(projectTasks, item => {
    return item?.task?.submission?.type == SUBMISSION_TYPE_DRAFT;
  });

  const {
    latestSubmissionResponse: {
      itemResponses: draftSubmissionItemResponses,
      isLocked: isDraftSubmissionLocked,
      isTurnedIn: isDraftSubmissionTurnedIn,
      submission: projectGroupSubmission,
    },
  } = draftProjectTask || {};

  const draftSubmissionItemResponseId = _.get(
    _.find(draftSubmissionItemResponses, item => {
      return item?.submissionItem?.fieldUID == "draftReport";
    }),
    "id"
  );

  const isUseTemplateDisabled = isUserStudentOrParent
    ? isDraftSubmissionTurnedIn || projectDisabled || isDraftSubmissionLocked
    : projectDisabled || isDraftSubmissionLocked;

  const onUseTemplateClick = async () => {
    setShowFullScreenLoader(true);
    const { error } = await createProjectTemplateCopy({
      projectGroupReportTemplateId: selectedProjectReportTemplate?.id,
      projectGroupId,
      submissionItemResponseId: draftSubmissionItemResponseId,
      getProjectIBDefinedTasksFilters,
    });
    if (error) {
      showUseTemplateErrors(error);
    }

    setShowFullScreenLoader(false);
  };

  const getPreHeaderComponent = () => {
    const { fieldUID } = _.get(projectGroupSubmission, "0", {});
    const fieldDetails = _.get(projectGroupSubmission, "0.fieldConfig", {})?.[
      fieldUID
    ];
    const {
      config: { label, subText },
    } = fieldDetails;

    switch (fieldUID) {
      case "reportSubmissionTable": {
        return (
          selectedProjectReportTemplate && (
            <div className={classes.submissionTemplateContainer}>
              <div
                className={classNames("text-label-l", classes.submissionTitle)}
              >
                {t("project:submission_template")}
              </div>

              <UseTemplateMediaCard
                selectedProjectReportTemplate={selectedProjectReportTemplate}
                containerStyle={styles.useTemplateContainerStyle}
                onUseTemplateClick={onUseTemplateClick}
                disabled={isUseTemplateDisabled}
              />

              <div
                className={classNames(
                  "text-body-s",
                  classes.templateSubtext,
                  classes.subtleText
                )}
              >
                {t("project:report_template_subtext")}
              </div>
            </div>
          )
        );
      }

      case "rationaleTable":
      case "documentationTable": {
        const downloadTemplateConfig = _.get(
          projectGroupSubmission,
          `[0].fieldConfig.${fieldUID}.config.downloadTemplateConfig`,
          {}
        );

        if (!_.isEmpty(downloadTemplateConfig)) {
          return (
            <div className={classes.documentationContainer}>
              <div className={"text-label-l"}>{label}</div>
              <div className={"text-body"}>{subText}</div>
              <div className={classes.bottomContainer}>
                {renderBottomComp && renderBottomComp}
              </div>
            </div>
          );
        }
      }
    }
  };

  return (
    <DSProvider dsVersion="2.0">
      <div className={classes.container} style={containerStyle}>
        {showHeaderContainer && (
          <div className={classes.headerContainer}>
            <div className={classes.headerLeft}>
              <Avatar
                icon={<JournalOutlined />}
                color="violet"
                dsVersion="2.0"
                iconType="icon"
                shape="square"
                size="large"
              />
              <h5 style={{ display: "inline" }}>{pageTitle}</h5>
            </div>
          </div>
        )}

        {getPreHeaderComponent()}
        <div className={classes.itemContainer}>
          {_.map(projectTasks, task => {
            const onAttachmentClick = ({
              selectedAttachmentId,
              isAnnotationEnabled,
            }) => {
              setViewModalDetails({
                selectedAttachmentId,
                taskId: task.id,
                displayModal: true,
                isAnnotationEnabled,
              });
            };

            return (
              <ReportItem
                projectTask={task}
                onUpdateProjectDeadline={onUpdateProjectDeadline}
                onUpdateProjectDeadlineSubmission={
                  onUpdateProjectDeadlineSubmission
                }
                onDeleteAttachment={onDeleteAttachment}
                onUpdateInputField={onUpdateInputField}
                onUpdateAttachment={onUpdateAttachment}
                onCreateAttachment={onCreateAttachment}
                onAttachmentClick={onAttachmentClick}
                projectDisabled={projectDisabled}
                isUserStudentOrParent={isUserStudentOrParent}
                projectId={projectId}
                autoCheckSimilarityRef={autoCheckSimilarityRef}
                projectGroupSubType={projectGroupSubType}
                progressOfUploads={progressOfUploads}
              />
            );
          })}
        </div>
      </div>
      {showFullScreenLoader && <FullScreenLoader />}
      {viewModalDetails?.displayModal && (
        <ReportSubmissionViewer
          {...viewModalDetails}
          projectTask={_.find(projectTasks, { id: viewModalDetails.taskId })}
          projectGroupType={projectGroupType}
          studentDetails={studentDetails}
          onCloseModal={onCloseModal}
          onDeleteAttachment={onDeleteAttachment}
          onUpdateInputField={onUpdateInputField}
          onUpdateAttachment={onUpdateAttachment}
          onCreateAttachment={onCreateAttachment}
          onUpdateProjectDeadline={onUpdateProjectDeadline}
          onUpdateProjectDeadlineSubmission={onUpdateProjectDeadlineSubmission}
          projectDisabled={projectDisabled}
          projectGroupSubType={projectGroupSubType}
          autoCheckSimilarityRef={autoCheckSimilarityRef}
          projectId={projectId}
        />
      )}
    </DSProvider>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    router: {
      params: { projectId },
    },
    projectDetails,
  } = ownProps;

  const projectGroupDetails = getCurrentProjectGroupDetails({ state });
  const {
    type: projectGroupType,
    subType: projectGroupSubType,
  } = projectGroupDetails;

  const userType = getUserType(state);
  const isUserStudentOrParent = isUserTypeStudentOrParent({ userType });

  const progressOfUploads = _.get(
    state,
    "app_services.progressOfUploads",
    EMPTY_UPLOADS
  );

  return {
    projectGroupType,
    projectGroupSubType,
    projectId,
    studentDetails: _.get(projectDetails, "studentsList", []),
    userType,
    isUserStudentOrParent,
    progressOfUploads,
    getProjectIBDefinedTasksFilters: {
      id: projectId,
      projectTaskFilters: {
        submissionTypes: [
          SUBMISSION_TYPE_IB_DEFINED,
          SUBMISSION_TYPE_FINAL,
          SUBMISSION_TYPE_DRAFT,
        ],
      },
    },
  };
};

const mapActionCreators = {
  updateProjectDeadlineSubmission,
  updateProjectDeadline,
  deleteAttachment,
  updateAttachment,
  createAttachment,
  updateSubmissionItemResponse,
  showUseTemplateErrors,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapActionCreators),
  graphql(getProjectIBDefinedTasksQuery, {
    name: "getProjectIBDefinedTasks",
    options: ({ getProjectIBDefinedTasksFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: getProjectIBDefinedTasksFilters,
    }),
    props({ getProjectIBDefinedTasks, ownProps: { template } }) {
      const data = _.get(getProjectIBDefinedTasks, "node");

      const updatedData = _.map(data?.tasks, task => {
        const { latestSubmissionResponse } = task;
        const { submission: projectGroupSubmission } =
          latestSubmissionResponse || {};
        const updatedProjectGroupSubmission = mapProjectGroupSubmissionWithFields(
          {
            projectGroupSubmissions: [projectGroupSubmission],
            template,
          }
        );

        return {
          ...task,
          latestSubmissionResponse: {
            ...latestSubmissionResponse,
            submission: updatedProjectGroupSubmission,
          },
        };
      });

      return {
        isData: !_.isEmpty(data),
        projectTasks: updatedData,
        isLoading:
          getProjectIBDefinedTasks["networkStatus"] == 1 ||
          getProjectIBDefinedTasks["networkStatus"] == 2,
        onPusherEventTriggered: params => {
          switch (params?.event) {
            case "attachmentSimilarityReportUpdate":
              if (_.isEqual(params?.entityType, "PROJECT")) {
                getProjectIBDefinedTasks.refetch();
              }
              break;

            case "projectSubmissionLockingStateUpdated":
              getProjectIBDefinedTasks.refetch();
              break;
            default:
              return;
          }
        },
      };
    },
  }),
  withPusherBind([
    "attachmentSimilarityReportUpdate",
    "projectSubmissionLockingStateUpdated",
  ]),
  withLoader
)(ReportV2);
